import { Button, Flex, HStack, Text } from '@chakra-ui/react';
import { GenericSubmissionField } from './MySubmission/FormFieldGeneric/GenericSubmissionField';
import { PhaseBanner } from './PhaseBanner';
import { CommandResponseProvider } from 'src/contexts/SourceFileCommandResponseContex';
import GroupRenderer from './MySubmission/FormFieldGeneric/GroupRenderer';
import { OutlineCard as AlembicOutlineCard, useToast } from '@morressier/alembic';
import { faEnvelope, faFileLines } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import NotifyAuthorConfirmationModal from './MySubmission/components/NotifyAuthorConfirmationModal';
import ProtectedWorkflowAction from '../ProtectedAction/ProtectedWorkflowAction';
import PreferencesTab from './PreferencesTab/PreferencesTab';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { useGenerateRevisionConsolidatedPdf } from 'src/api/mutations';
import { fetchRevisionConsolidatedPdfStatus } from 'src/api/queries';
import { getConsolidatedPdfErrorMessage } from 'src/pages/Organization/public/Journal/SubmissionUpload/consolidatedPdfError';
import ConsolidatedPdfGenerationProgressModal, {
  ConsolidatedPdfGenerationStatus
} from 'src/pages/Organization/public/Journal/SubmissionUpload/components/ConsolidatedPdfGenerationProgressModal';
import ConsolidatedPdfPreviewModal from 'src/pages/Organization/public/Journal/SubmissionUpload/components/ConsolidatedPdfPreviewModal';
import { ISubmissionFileSet } from '@publishing-engine/domain/models';

export const OutlineCard: React.FC<{ children: React.ReactNode }> = ({
  children
}) => (
  <AlembicOutlineCard
    borderless
    borderRadius="none"
    flexDirection="column"
    justifyContent="space-between"
    gap={4}
  >
    {children}
  </AlembicOutlineCard>
);

const SubmissionTab: React.FC<{}> = () => {
  const [showNotifyAuthor, setShowNotifyAuthor] = useState(false);
  const [generationStatus, setGenerationStatus] =
    useState<ConsolidatedPdfGenerationStatus>(null);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewFileSet, setPreviewFileSet] = useState<Pick<
    ISubmissionFileSet,
    'ConsolidatedPdf' | 'AnonymizedConsolidatedPdf'
  > | null>(null);
  const toast = useToast();
  const {
    submissionId,
    submissionContextId,
    submissionContext,
    organizationId,
    fieldsResponse,
    refetchSubmission
  } = useSubmissionData();
  const generateConsolidatedPdfMutation = useGenerateRevisionConsolidatedPdf();
  const enableConsolidatedPdf =
    submissionContext?.config?.enableConsolidatedPdf;
  const isFileUpdated = fieldsResponse?.isFileUpdated === true;
  const isGeneratingPdf =
    generationStatus === 'generating' || generationStatus === 'refreshing';

  const handleGenerateConsolidatedPdf = async (rerun = false) => {
    if (!submissionContextId || !submissionId) return;
    setGenerationError(null);
    setGenerationStatus('generating');
    try {
      const result = await generateConsolidatedPdfMutation.mutateAsync({
        submissionContextId,
        submissionId,
        rerun
      });

      if (!result.isSuccess) {
        const errorMessage = getConsolidatedPdfErrorMessage(result.message);
        setGenerationStatus('error');
        setGenerationError(errorMessage || 'Please try again.');
        toast({
          variant: 'error',
          title: 'Consolidated PDF generation failed',
          description: errorMessage || 'Please try again.',
          isClosable: true
        });
        return;
      }

      setGenerationStatus('refreshing');

      let status = await fetchRevisionConsolidatedPdfStatus(
        submissionContextId,
        submissionId
      );
      while (status.status !== 'completed') {
        if (status.status === 'failed') {
          setGenerationStatus('error');
          setGenerationError(status.error || 'Please try again.');
          toast({
            variant: 'error',
            title: 'Consolidated PDF generation failed',
            description: status.error || 'Please try again.',
            isClosable: true
          });
          return;
        }

        await new Promise(resolve => setTimeout(resolve, 5000));
        status = await fetchRevisionConsolidatedPdfStatus(
          submissionContextId,
          submissionId
        );
      }

      refetchSubmission();
      setGenerationStatus(null);
      setPreviewFileSet({
        ConsolidatedPdf: status.file,
        AnonymizedConsolidatedPdf: status.anonymizedFile
      });
      setIsPreviewModalOpen(true);
    } catch (error) {
      const errorMessage = getConsolidatedPdfErrorMessage(error);
      setGenerationStatus('error');
      setGenerationError(errorMessage || 'Please try again.');
      toast({
        variant: 'error',
        title: 'Consolidated PDF generation failed',
        description: errorMessage || 'Please try again.',
        isClosable: true
      });
    }
  };

  const handleExitConsolidatedPdfProgress = () => {
    setGenerationStatus(null);
    setGenerationError(null);
  };

  return (
    <Flex flexDirection="column" gap={5}>
      <PhaseBanner role="editorial" />

      <Flex justifyContent="space-between" alignItems="center">
        <Flex flexDirection="column" alignItems="flex-start" gap={4}>
          <Text textStyle="headingSection">Submission</Text>
          <Text textStyle="textBody">
            Review and manage all information associated with this submission.
          </Text>
        </Flex>

        <HStack spacing={2}>
          {enableConsolidatedPdf && (
            <ProtectedWorkflowAction actionName="EditSubmission">
              <Button
                variant="primary"
                colorScheme="blue"
                leftIcon={<FontAwesomeIcon icon={faFileLines} />}
                fontSize="sm"
                onClick={() => handleGenerateConsolidatedPdf()}
                isDisabled={!isFileUpdated || isGeneratingPdf}
              >
                Generate PDFs
              </Button>
            </ProtectedWorkflowAction>
          )}
          <ProtectedWorkflowAction actionName="EditSubmission">
            <Button
              variant="secondary"
              colorScheme="blue"
              leftIcon={<FontAwesomeIcon icon={faEnvelope} />}
              onClick={() => setShowNotifyAuthor(true)}
            >
              Notify Author
            </Button>
          </ProtectedWorkflowAction>
        </HStack>
      </Flex>

      <GenericSubmissionField fieldName="FocusIssue" />
      <GenericSubmissionField fieldName="Title" />
      <GenericSubmissionField fieldName="EnglishTitle" />
      <GenericSubmissionField fieldName="Subtitle" />
      <GenericSubmissionField fieldName="EnglishSubtitle" />
      <GenericSubmissionField fieldName="ArticleType" />
      <GenericSubmissionField fieldName="Abstract" />
      <GenericSubmissionField fieldName="EnglishAbstract" />
      <GenericSubmissionField fieldName="Keywords" />
      <GenericSubmissionField fieldName="SubmissionGuidelines" />
      <GenericSubmissionField fieldName="EnglishKeywords" />
      <GenericSubmissionField fieldName="ArticleSubtype" />
      <GenericSubmissionField fieldName="ArticleLanguage" />
      <GenericSubmissionField fieldName="Category" />
      <GenericSubmissionField fieldName="CategoryOther" />
      <GenericSubmissionField fieldName="FiguresAndTables" />

      <Flex flexDirection="column" gap={4}>
        <Flex justifyContent="space-between">
          <Text textStyle="headingSection">Submission Files</Text>
        </Flex>
        <Flex flexDirection={'column'} gap={5}>
          <CommandResponseProvider>
            <GenericSubmissionField fieldName="SourceFiles" />
          </CommandResponseProvider>
          <GenericSubmissionField fieldName="Manuscript" />
          <GenericSubmissionField fieldName="TitlePage" />
          <GenericSubmissionField fieldName="ConsolidatedPdf" />
          <GenericSubmissionField fieldName="AnonymizedConsolidatedPdf" />
          <GenericSubmissionField fieldName="CompleteDocumentForReview" />
          <GenericSubmissionField fieldName="AnonymizedManuscript" />
          <GenericSubmissionField
            fieldName="Supplementary"
            showSubtitle={false}
          />
          <GenericSubmissionField
            fieldName="AdditionalSupplementary"
            showSubtitle={false}
          />
          <GroupRenderer
            showSubtitle={false}
            fields={[
              {
                name: 'CoverLetter',
                renderMethod: element => <OutlineCard>{element}</OutlineCard>
              },
              {
                name: 'StandardReportingGuidelines',
                renderMethod: el => <OutlineCard>{el}</OutlineCard>
              }
            ]}
          >
            {renderedFields => (
              <Flex gap={8} flexDirection="column">
                {renderedFields}
              </Flex>
            )}
          </GroupRenderer>
          <GroupRenderer
            fields={[
              {
                name: 'Figures',
                renderMethod: el => <OutlineCard>{el}</OutlineCard>
              },
              {
                name: 'GraphicalAbstractImage',
                renderMethod: el => <OutlineCard>{el}</OutlineCard>
              },
              {
                name: 'Tables',
                renderMethod: el => <OutlineCard>{el}</OutlineCard>
              }
            ]}
          >
            {renderedFields => (
              <Flex gap={8} flexDirection="column">
                {renderedFields}
              </Flex>
            )}
          </GroupRenderer>
          <GenericSubmissionField fieldName="NumberOfFigures" />
          <GenericSubmissionField fieldName="NumberOfColorFigures" />
          <GenericSubmissionField fieldName="NumberOfTables" />
          <GenericSubmissionField fieldName="NumberOfWords" />
          <GenericSubmissionField fieldName="GeneralClassifications" />
        </Flex>
        <PreferencesTab />
      </Flex>

      <NotifyAuthorConfirmationModal
        isOpen={showNotifyAuthor}
        onClose={() => setShowNotifyAuthor(false)}
      />
      <ConsolidatedPdfGenerationProgressModal
        isOpen={generationStatus !== null}
        isGenerating={isGeneratingPdf}
        generationStatus={generationStatus}
        generationError={generationError}
        onExit={handleExitConsolidatedPdfProgress}
        onRetry={() => handleGenerateConsolidatedPdf(true)}
      />
      {previewFileSet && (
        <ConsolidatedPdfPreviewModal
          isOpen={isPreviewModalOpen}
          onClose={() => setIsPreviewModalOpen(false)}
          fileSet={previewFileSet}
          submissionContextId={submissionContextId}
          organizationId={organizationId}
        />
      )}
    </Flex>
  );
};

export default SubmissionTab;
